import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from '../utils/axios';
import { default as plainAxios } from 'axios';
import { isValidToken, setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
};

const handlers = {
  SET_USER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    user: null,
    company: null,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  getUser: () => Promise.resolve(),
  registerEmployee: () => Promise.resolve(),
  requestPhoneCert: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          await getUser();
        } else {
          dispatch({
            type: 'SET_USER',
            payload: {
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'SET_USER',
          payload: {
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/api/accounts/login/', {
      email,
      password,
    });
    const { access, refresh } = response.data;
    setSession(access, refresh);
    await getUser();
  };

  const register = async (email, password, allowMarketing, from, turnstileToken) => {
    await plainAxios.post(`${process.env.HOST_API_KEY}/api/accounts/register/`, {
      email,
      password,
      register_type: 'employee',
      allow_email: allowMarketing,
      allow_sms: allowMarketing,
      lead: from,
      turnstile_token: turnstileToken,
    });
    await login(email, password);
  };

  const logout = async () => {
    setSession(null);
    if (window.dataLayer) {
      window.dataLayer.push({"user_id": "", "event": "setUserId"});
    }
    dispatch({ type: 'LOGOUT' });
  };

  const requestPhoneCert = async (phone) => {
    const response = await axios.post('/api/accounts/cert/phone/generate/', {
      reason: 'register',
      target_value: phone,
    });
    return response.status;
  }

  const getUser = async () => {
    const responseMe = await axios.get('api/accounts/me/employee/');
    dispatch({
      type: 'SET_USER',
      payload: {
        user: responseMe.data
      },
    });
    return responseMe.data;
  }

  const registerEmployee = async (name, phone, phonecert, company, serviceDetail, referralCode) => {
    await axios.post('/api/accounts/register/employee/', {
      username: name,
      cert_code: phonecert,
      phone,
      company,
      service_detail: serviceDetail,
      referral_code: referralCode || null,
    });

    await getUser();
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        getUser,
        registerEmployee,
        requestPhoneCert,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
